import { SuggestionMenu } from "@circle-react-shared/SuggestionMenu";
import type { SuggestionItem } from "@circle-react-shared/SuggestionMenu/types";
import type { SuggestionListHandle } from "@circle-react-shared/SuggestionMenu/useSuggestionListKeyboard";
import { useSuggestionListKeyboard } from "@circle-react-shared/SuggestionMenu/useSuggestionListKeyboard";
import { useEmojiMartData } from "@circle-react-shared/uikit/EmojiPickerV3/Pickers/EmojiMart/useEmojiMartData";
import { useInternalEditorState } from "@circle-react-shared/uikit/TipTap/internalEditorState";
import { useTipTapEditorContext } from "@circle-react-uikit/TipTap/TipTapEditorContext";
import { useFetchEmojis } from "@circle-react-uikit/TipTapBlockEditor/InlineEmojiExtension/hooks/useFetchEmojis";
import type { Editor, Range } from "@tiptap/core";
import { forwardRef, useEffect, useMemo } from "react";

interface EmojiEntry {
  id: string;
  skins: { native: string; shortcodes: string }[];
}

export interface CompassEmojiListProps {
  clientRect?: (() => DOMRect | null) | null;
  editor: Editor;
  query: string;
  range: Range;
}

export const CompassEmojiList = forwardRef<
  SuggestionListHandle,
  CompassEmojiListProps
>(({ clientRect, editor, query, range }, ref) => {
  const { setPopupsVisible } = useInternalEditorState();
  const tiptapEditorContext = useTipTapEditorContext();

  useEmojiMartData();

  const { emojis } = useFetchEmojis(query);

  const { isOpen, menuRef } = useSuggestionListKeyboard({
    ref,
    query,
    isEnabled: emojis.length > 0,
  });

  useEffect(() => {
    setPopupsVisible(isOpen);

    return () => setPopupsVisible(false);
  }, [isOpen, setPopupsVisible]);

  const insertEmoji = (emoji: EmojiEntry) => {
    editor
      .chain()
      .focus()
      .deleteRange(range)
      .insertContent(emoji.skins[0].native)
      .insertContent(" ")
      .run();
  };

  const groups = useMemo(() => {
    if (emojis.length === 0) return [];

    return [
      {
        id: "emojis",
        items: (emojis as EmojiEntry[]).map(emoji => ({
          id: emoji.id,
          label: emoji.skins[0].shortcodes,
          visual: <span className="text-base">{emoji.skins[0].native}</span>,
          data: emoji,
        })),
      },
    ];
  }, [emojis]);

  const onSelect = (item: SuggestionItem) => {
    insertEmoji(item.data as EmojiEntry);
    setPopupsVisible(false);
  };

  return (
    <SuggestionMenu
      ref={menuRef}
      open={isOpen}
      groups={groups}
      getAnchorRect={() => clientRect?.() ?? null}
      anchorContextElement={editor?.view.dom}
      onSelect={onSelect}
      side={tiptapEditorContext ? "top" : "bottom"}
    />
  );
});

CompassEmojiList.displayName = "CompassEmojiList";
